import React from "react";

// Per-route SEO tags. Uses React 19's native document-metadata support: the
// <title>/<meta>/<link> below are automatically hoisted into <head>, and are
// swapped out when the route changes. index.html deliberately carries NO
// page-level title/description/OG/Twitter/canonical so there is exactly one of
// each per route (no duplicates for crawlers). Prerendering (scripts/prerender.js)
// bakes these into each route's served HTML.

export const SITE_URL = "https://getjeweldesigns.com";
export const SITE_NAME = "GetJewelDesigns";
const DEFAULT_TITLE = "GetJewelDesigns — Bespoke Jewellery Design";
const DEFAULT_DESCRIPTION =
  "Managed jewellery CAD studio. Bespoke rings, pendants, bracelets, earrings and necklaces designed by a discreet Design Team.";
// 1200x630. twitter:card is summary_large_image, and Facebook/WhatsApp crop to
// roughly 1.91:1 — a square icon gets its top and bottom sliced off, so the
// share card is a purpose-built landscape image rather than the app icon.
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

// Normalise a route path into an absolute canonical URL.
function canonicalFor(path) {
  if (!path || path === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
  const url = canonicalFor(path);
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      {/*
        Dimensions are only declared for the default share card, whose size we
        actually know (1200x630). Pages that pass their own image (blog covers,
        category photos) get arbitrary dimensions from Pexels/Unsplash, and
        stating the wrong ones is worse than stating none: unfurlers lay out a
        placeholder from these numbers and the image then jumps or crops.
      */}
      {image === DEFAULT_IMAGE && (
        <>
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      )}
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {blocks.map((data, i) => (
        <script
          key={i}
          type="application/ld+json"
          // JSON-LD is inert data, not executed script; safe to inline.
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </>
  );
}

// Stable node ids for the studio and the site. The full Organization is described
// once, on the home page. Other pages point at it by @id, but every reference
// also carries `name` and `url` inline: Google does not promise to resolve an
// @id against a node that lives on a different URL, so a bare {"@id"} on a blog
// post would be an anonymous organisation to most parsers.
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const ORG_REF = { "@type": "Organization", "@id": ORG_ID, name: SITE_NAME, url: `${SITE_URL}/` };
const WEBSITE_REF = { "@type": "WebSite", "@id": WEBSITE_ID, name: SITE_NAME, url: `${SITE_URL}/` };

// Site-wide Organization + WebSite structured data (rendered once, on the home page).
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/icon-512.png`,
  description: DEFAULT_DESCRIPTION,
  email: "getjeweldesigns@gmail.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Surat",
    addressRegion: "Gujarat",
    addressCountry: "IN",
  },
  // Already disclosed in prose on /about, so naming the proprietor here reveals
  // nothing new — it just makes the studio an entity a search engine can tie to
  // a real, accountable person. This is the operator of the business, NOT one of
  // the design specialists, whose anonymity is the product and stays intact.
  founder: {
    "@type": "Person",
    name: "Sonam Patel",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "getjeweldesigns@gmail.com",
    areaServed: "Worldwide",
    availableLanguage: ["en"],
  },
  // No `sameAs`. It is the single highest-value addition left on this object,
  // but it must point at profiles that actually exist and are verifiably the
  // same entity — a Google Business Profile and a LinkedIn company page. Add the
  // real URLs here once they are live; inventing them would be worse than the gap.
};

export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  inLanguage: "en-GB",
  // Resolves on the same page: the home page emits ORGANIZATION_JSONLD alongside.
  publisher: { "@id": ORG_ID },
  // No potentialAction/SearchAction: the site has no search results URL to
  // point it at, and Google retired the sitelinks search box in 2024.
};

// The studio's offering, stated as a Service rather than upgrading Organization
// to LocalBusiness/ProfessionalService: those subtypes imply a place a customer
// can visit, and this is a remote B2B studio. `areaServed` carries the reach that
// the PostalAddress on Organization would otherwise seem to narrow to Surat.
export const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Jewellery CAD design",
  serviceType: "Jewellery CAD design",
  provider: ORG_REF,
  areaServed: "Worldwide",
  audience: {
    "@type": "BusinessAudience",
    audienceType: "Jewellers, retailers and independent designers",
  },
  description:
    "Managed jewellery CAD studio producing production-ready STL, STEP and OBJ files for rings, pendants, bracelets, earrings and necklaces.",
};

export const CONTACT_PAGE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: `${SITE_URL}/contact`,
  name: `Contact — ${SITE_NAME}`,
  about: { ...ORG_REF, email: "getjeweldesigns@gmail.com" },
};

// The journal index. No ItemList of the posts: Google needs none for a
// CollectionPage, and a hand-maintained list here would drift from blog.js.
export const COLLECTION_PAGE_JSONLD = (path, name) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  url: `${SITE_URL}${path}`,
  name,
  isPartOf: WEBSITE_REF,
});

// BreadcrumbList for a detail page, so a search result can show
// "getjeweldesigns.com › Categories › Ring" in place of a bare URL.
// `trail` is [{ name, path }] ordered from the site root inwards, the last
// entry being the current page.
export const BREADCRUMB_JSONLD = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((crumb, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: crumb.name,
    item: `${SITE_URL}${crumb.path}`,
  })),
});

// FAQPage for a page that shows its questions and answers in the visible HTML.
// `faqs` is [{ q, a }], with `a` as plain text.
export const FAQ_JSONLD = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
